
import React, { useState, useCallback } from "react";
import { GoogleMap, LoadScript, Marker, Polyline, InfoWindow, StandaloneSearchBox } from "@react-google-maps/api";
import { C, RC, RF } from "../utils/api";

const LIBS = ["places"];

const FIBRA_COLORS = {
  "96": "#EF4444", "48": "#F97316", "24": "#1E7BBF",
  "12": "#10B981", "8": "#06B6D4", "6": "#8B5CF6",
};

const ICONS = {
  olt: "https://maps.google.com/mapfiles/kml/pal3/icon47.png",
  nap_activo: "https://maps.google.com/mapfiles/kml/paddle/grn-square.png",
  nap_danado: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
  nap_mantenimiento: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
  mufa_activo: "https://maps.google.com/mapfiles/kml/paddle/pink-diamond.png",
  mufa_danado: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
};

function haversine(p1, p2) {
  const R = 6371000;
  const dLat = (p2.lat - p1.lat) * Math.PI / 180;
  const dLng = (p2.lng - p1.lng) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(p1.lat*Math.PI/180) * Math.cos(p2.lat*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

function calcDistancia(points) {
  let d = 0;
  for(let i=1; i<points.length; i++) d += haversine(points[i-1], points[i]);
  return Math.round(d);
}

function getEquiposEnRuta(points, equipos, threshold=0.0003) {
  const all = [
    ...(equipos.olts||[]).map(e=>({...e,tipo:"olt"})),
    ...(equipos.mufas||[]).map(e=>({...e,tipo:"mufa"})),
    ...(equipos.naps||[]).map(e=>({...e,tipo:"nap"})),
  ];
  const encontrados = [];
  for(const pt of points){
    const eq = all.find(e=>Math.abs(parseFloat(e.lat)-pt.lat)<threshold && Math.abs(parseFloat(e.lng)-pt.lng)<threshold);
    if(eq && !encontrados.find(x=>x.id===eq.id && x.tipo===eq.tipo)) encontrados.push(eq);
  }
  return encontrados;
}

function calcReservas(points, equipos) {
  const eqs = getEquiposEnRuta(points, equipos);
  let reserva = 10; // inicio
  for(let i=1; i<eqs.length-1; i++) reserva += 20; // intermedios
  if(eqs.length>1) reserva += 20; // ultimo dispositivo o punta
  else reserva += 10; // punta libre
  return reserva;
}

function getEquipoAt(lat, lng, equipos, threshold=0.0003) {
  const all = [
    ...(equipos.olts||[]).map(e=>({...e,tipo:"olt"})),
    ...(equipos.mufas||[]).map(e=>({...e,tipo:"mufa"})),
    ...(equipos.naps||[]).map(e=>({...e,tipo:"nap"})),
  ];
  return all.find(e=>Math.abs(parseFloat(e.lat)-lat)<threshold && Math.abs(parseFloat(e.lng)-lng)<threshold) || null;
}

export default function Mapa({equipos, cfg, addMode, setAddMode, setForm, sel, setSel, mapCenter, setMapCenter, mapZoom, setMapZoom, user, loadCl, clientes, delEq, fibraSelId, setFibraSelId, onVerDiagrama, onVerDiagramaMufa}) {
  const [searchBox, setSearchBox] = useState(null);
  const mapRef = React.useRef(null);

  React.useEffect(()=>{
    if(mapRef.current){
      mapRef.current.panTo(mapCenter);
      mapRef.current.setZoom(mapZoom);
    }
  },[mapCenter, mapZoom]);
  const [fibraPoints, setFibraPoints] = useState([]);
  const [fibraOrigen, setFibraOrigen] = useState(null);
  const [fibraInfo, setFibraInfo] = useState(null);
  const [editingFibra, setEditingFibra] = useState(null);
  const modoFibra = addMode === "fibra";

  const onPlacesChanged = ()=>{
    const places = searchBox.getPlaces();
    if(places&&places.length>0){
      const loc = places[0].geometry.location;
      setMapCenter({lat:loc.lat(), lng:loc.lng()});
      setMapZoom(16);
    }
  };

  const onMapClick = useCallback((e)=>{
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    if(modoFibra){
      setFibraPoints(pts=>[...pts, {lat, lng}]);
      return;
    }
    if(!addMode) return;
    setForm({tipo:addMode, lat, lng});
    setAddMode(null);
  },[addMode, modoFibra, setAddMode, setForm]);

  const onMarkerClick = useCallback((equipo)=>{
    if(modoFibra){
      const pt = {lat:parseFloat(equipo.lat), lng:parseFloat(equipo.lng)};
      setFibraPoints(pts=>{
        if(pts.length===0) setFibraOrigen({tipo:equipo.tipo, id:equipo.id});
        return [...pts, pt];
      });
      return;
    }
    setSel(equipo);
    if(equipo.tipo==="nap") loadCl(equipo.id);
  },[modoFibra, setSel, loadCl]);

  const finishFibra = ()=>{
    if(fibraPoints.length < 2){ alert("Dibuja al menos 2 puntos"); return; }
    const lastPt = fibraPoints[fibraPoints.length-1];
    const destino = getEquipoAt(lastPt.lat, lastPt.lng, equipos);
    if(editingFibra){
      setForm({tipo:"fibra", edit:editingFibra, coordenadas:fibraPoints, origen:fibraOrigen, destino:destino?{tipo:destino.tipo,id:destino.id}:null});
      setEditingFibra(null);
    } else {
      setForm({tipo:"fibra", coordenadas:fibraPoints, origen:fibraOrigen, destino:destino?{tipo:destino.tipo,id:destino.id}:null});
    }
    setFibraPoints([]); setFibraOrigen(null); setAddMode(null);
  };

  const cancelFibra = ()=>{ setFibraPoints([]); setFibraOrigen(null); setEditingFibra(null); setAddMode(null); };
  const undoPoint = ()=>setFibraPoints(pts=>pts.slice(0,-1));

  const onFibraClick = (fibra)=>{
    setFibraSelId(fibra.id);
    if(modoFibra) return;
    try {
      const coords = fibra.coordenadas ? JSON.parse(fibra.coordenadas) : [];
      const eqs = getEquiposEnRuta(coords, equipos);
      const distReal = calcDistancia(coords);
      const reserva = calcReservas(coords, equipos);
      setFibraInfo({fibra, coords, eqs, distReal, distTotal: distReal + reserva, reserva});
    } catch(e){}
  };

  const editarFibra = (info)=>{
    try {
      const coords = info.fibra.coordenadas ? JSON.parse(info.fibra.coordenadas) : [];
      setFibraPoints(coords);
      setFibraOrigen(info.eqs.length>0 ? {tipo:info.eqs[0].tipo, id:info.eqs[0].id} : null);
      setEditingFibra(info.fibra);
      setFibraInfo(null);
      setAddMode("fibra");
    } catch(e){}
  };

  const lines = (equipos.fibras||[]).map(f=>{
    try {
      const coords = f.coordenadas ? JSON.parse(f.coordenadas) : null;
      const color = f.estado==="danado" ? "#EF4444" : (FIBRA_COLORS[f.tipo_hilos] || "#1E7BBF");
      if(coords&&coords.length>1) return {id:f.id, path:coords, color, fibra:f};
      const all=[...(equipos.olts||[]),...(equipos.mufas||[]),...(equipos.naps||[])];
      const o=all.find(x=>x.id==f.origen_id);
      const d=all.find(x=>x.id==f.destino_id);
      if(!o||!d) return null;
      return {id:f.id, path:[{lat:parseFloat(o.lat),lng:parseFloat(o.lng)},{lat:parseFloat(d.lat),lng:parseFloat(d.lng)}], color, fibra:f};
    } catch(e){ return null; }
  }).filter(Boolean);

  const getIcon=(tipo,estado)=>{
    if(tipo==="olt") return ICONS.olt;
    if(tipo==="mufa") return estado==="danado"?ICONS.mufa_danado:ICONS.mufa_activo;
    return estado==="danado"?ICONS.nap_danado:estado==="mantenimiento"?ICONS.nap_mantenimiento:ICONS.nap_activo;
  };

  const tipoLabel = t => t==="olt"?"OLT":t==="mufa"?"Mufa":"NAP";

  return (
    <div style={{height:"100%",position:"relative"}}>
      {addMode && addMode!=="fibra" && (
        <div style={{position:"absolute",top:10,left:"50%",transform:"translateX(-50%)",background:"#F59E0B",color:"#000",padding:"8px 16px",borderRadius:20,fontSize:13,fontWeight:600,zIndex:20}}>
          Toca el mapa para agregar {addMode.toUpperCase()}
          <span onClick={()=>setAddMode(null)} style={{marginLeft:10,cursor:"pointer"}}>X</span>
        </div>
      )}
      {modoFibra && (
        <div style={{position:"absolute",top:10,left:"50%",transform:"translateX(-50%)",background:"#1E7BBF",color:"#fff",padding:"8px 12px",borderRadius:20,fontSize:12,fontWeight:600,zIndex:20,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",maxWidth:"90%"}}>
          {editingFibra && <span style={{background:"#F59E0B",color:"#000",padding:"2px 8px",borderRadius:8,fontSize:11}}>Editando</span>}
          <span>{fibraPoints.length===0?"Toca dispositivo o mapa para iniciar":fibraPoints.length+" puntos"}</span>
          {fibraPoints.length>0 && <span onClick={undoPoint} style={{background:"rgba(255,255,255,0.3)",padding:"3px 8px",borderRadius:10,cursor:"pointer",fontSize:11}}>Deshacer</span>}
          {fibraPoints.length>=2 && <span onClick={finishFibra} style={{background:"#10B981",padding:"3px 10px",borderRadius:10,cursor:"pointer",fontSize:11}}>Terminar</span>}
          <span onClick={cancelFibra} style={{background:"rgba(255,255,255,0.2)",padding:"3px 8px",borderRadius:10,cursor:"pointer",fontSize:11}}>Cancelar</span>
        </div>
      )}

      <div style={{position:"absolute",bottom:70,right:10,background:"rgba(255,255,255,0.95)",borderRadius:8,padding:"8px 10px",zIndex:10,fontSize:11,boxShadow:"0 2px 6px rgba(0,0,0,0.15)"}}>
        {Object.entries(FIBRA_COLORS).map(([h,c])=>(
          <div key={h} style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
            <div style={{width:20,height:4,background:c,borderRadius:2}}/>
            <span>{h} hilos</span>
          </div>
        ))}
      </div>

      {fibraInfo && (
        <div style={{position:"absolute",bottom:70,left:10,background:"#fff",borderRadius:10,padding:"14px",zIndex:20,maxWidth:280,boxShadow:"0 4px 16px rgba(0,0,0,0.2)"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
            <p style={{margin:0,fontWeight:700,fontSize:14}}>{fibraInfo.fibra.nombre}</p>
            <span onClick={()=>{setFibraInfo(null);setFibraSelId(null);}} style={{cursor:"pointer",color:"#999",fontSize:16}}>×</span>
          </div>
          <p style={{margin:"0 0 4px",fontSize:12,color:"#666"}}>{fibraInfo.fibra.tipo_hilos} hilos - {fibraInfo.fibra.tipo_instalacion}</p>
          <div style={{margin:"8px 0",padding:"8px",background:"#F5F7FA",borderRadius:8}}>
            <p style={{margin:"0 0 4px",fontSize:12,fontWeight:600}}>Recorrido:</p>
            {fibraInfo.eqs.length>0 ? (
              <p style={{margin:0,fontSize:12,color:"#444"}}>{fibraInfo.eqs.map(e=>tipoLabel(e.tipo)+": "+e.nombre).join(" → ")}</p>
            ) : (
              <p style={{margin:0,fontSize:12,color:"#999"}}>Sin dispositivos detectados</p>
            )}
          </div>
          <div style={{margin:"8px 0",padding:"8px",background:"#EFF6FF",borderRadius:8}}>
            <p style={{margin:"0 0 4px",fontSize:12,fontWeight:600}}>Distancia:</p>
            <p style={{margin:"0 0 2px",fontSize:12}}>Real: <strong>{fibraInfo.distReal} m</strong></p>
            <p style={{margin:"0 0 2px",fontSize:12}}>Reservas: <strong>+{fibraInfo.reserva} m</strong></p>
            <p style={{margin:0,fontSize:13,fontWeight:700,color:"#1E7BBF"}}>Total: {fibraInfo.distTotal} m</p>
          </div>
          {RC.includes(user.rol) && (
            <div style={{display:"flex",gap:8,marginTop:8}}>
              <button onClick={()=>editarFibra(fibraInfo)} style={{flex:1,padding:"6px 0",background:"#1E7BBF",color:"#fff",border:"none",borderRadius:6,fontSize:12,cursor:"pointer"}}>Editar trazado</button>
              <button onClick={()=>{ setForm({tipo:"fibra",edit:fibraInfo.fibra}); setFibraInfo(null); }} style={{flex:1,padding:"6px 0",background:"#F5F7FA",color:"#333",border:"1px solid #E8ECF0",borderRadius:6,fontSize:12,cursor:"pointer"}}>Editar datos</button>
            </div>
          )}
        </div>
      )}

      <LoadScript googleMapsApiKey={cfg.google_maps_key} libraries={LIBS}>
        <StandaloneSearchBox onLoad={ref=>setSearchBox(ref)} onPlacesChanged={onPlacesChanged}>
          <input type="text" placeholder="Buscar..." style={{boxSizing:"border-box",border:"1px solid #E8ECF0",width:"140px",height:"32px",padding:"0 10px",borderRadius:8,boxShadow:"0 2px 6px rgba(0,0,0,0.15)",fontSize:12,outline:"none",position:"absolute",top:50,left:10,zIndex:10,background:"#fff"}}/>
        </StandaloneSearchBox>
        <GoogleMap mapContainerStyle={{width:"100%",height:"100%"}} center={mapCenter} zoom={mapZoom}
          onClick={onMapClick}
          onLoad={map=>mapRef.current=map}
          onIdle={(map)=>{if(map&&map.getCenter){setMapCenter({lat:map.getCenter().lat(),lng:map.getCenter().lng()});setMapZoom(map.getZoom());}}}
          options={{streetViewControl:false,mapTypeControl:true,fullscreenControl:false}}>

          {fibraPoints.length>1 && <Polyline path={fibraPoints} options={{strokeColor:"#1E7BBF",strokeWeight:3,strokeOpacity:0.9}}/>}
          {fibraPoints.map((p,i)=>(<Marker key={"fp-"+i} position={p} icon="https://maps.google.com/mapfiles/ms/icons/ltblue-dot.png" zIndex={999}/>))}

          {(equipos.olts||[]).map(o=>(<Marker key={"olt-"+o.id} position={{lat:parseFloat(o.lat),lng:parseFloat(o.lng)}} title={o.nombre} icon={getIcon("olt",o.estado)} onClick={()=>onMarkerClick({...o,tipo:"olt"})}/>))}
          {(equipos.mufas||[]).map(m=>(<Marker key={"mufa-"+m.id} position={{lat:parseFloat(m.lat),lng:parseFloat(m.lng)}} title={m.nombre} icon={getIcon("mufa",m.estado)} onClick={()=>onMarkerClick({...m,tipo:"mufa"})}/>))}
          {(equipos.naps||[]).map(n=>(<Marker key={"nap-"+n.id} position={{lat:parseFloat(n.lat),lng:parseFloat(n.lng)}} title={n.nombre} icon={getIcon("nap",n.estado)} onClick={()=>onMarkerClick({...n,tipo:"nap"})}/>))}

          {lines.map(l=>(
            <React.Fragment key={"f-"+l.id}>
              {fibraSelId===l.id && <Polyline path={l.path} options={{strokeColor:"#FFFFFF",strokeWeight:10,strokeOpacity:0.4}}/>}
              <Polyline path={l.path}
                options={{strokeColor:fibraSelId===l.id?"#FFFF00":l.color, strokeWeight:fibraSelId===l.id?6:4, strokeOpacity:0.9, zIndex:fibraSelId===l.id?999:1}}
                onClick={()=>onFibraClick(l.fibra)}/>
            </React.Fragment>
          ))}

          {!modoFibra && sel && (
            <InfoWindow position={{lat:parseFloat(sel.lat),lng:parseFloat(sel.lng)}} onCloseClick={()=>setSel(null)}>
              <div style={{minWidth:180,fontFamily:"Roboto,sans-serif"}}>
                <p style={{margin:"0 0 4px",fontWeight:700,fontSize:14}}>{sel.nombre}</p>
                <p style={{margin:"0 0 4px",fontSize:12,color:"#666"}}>{(sel.tipo||"").toUpperCase()}</p>
                <p style={{margin:"0 0 8px",fontSize:12}}>Estado: <span style={{color:sel.estado==="activo"?"#10B981":"#EF4444",fontWeight:600}}>{sel.estado}</span></p>
                {sel.tipo==="nap" && <p style={{margin:"0 0 8px",fontSize:12}}>Puertos: {sel.puertos_ocupados||0}/{sel.puertos} - {sel.tipo_splitter}</p>}
                {clientes.length>0 && sel.tipo==="nap" && clientes.map(c=>(<p key={c.id} style={{margin:"0 0 2px",fontSize:11}}>Puerto {c.puerto}: {c.nombre}</p>))}
                <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:8}}>
                  {RF.includes(user.rol) && sel.tipo==="nap" && <button onClick={()=>setForm({tipo:"cliente",nap:sel})} style={{padding:"4px 8px",background:"#10B981",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>+ Cliente</button>}
                  {RF.includes(user.rol) && sel.tipo==="nap" && <button onClick={()=>onVerDiagrama(sel)} style={{padding:"4px 8px",background:"#7C3AED",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Ver diagrama</button>}
                  {RF.includes(user.rol) && sel.tipo==="mufa" && <button onClick={()=>onVerDiagramaMufa(sel)} style={{padding:"4px 8px",background:"#6D28D9",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Ver diagrama</button>}
                  {RF.includes(user.rol) && <button onClick={()=>setForm({tipo:"averia",equipo:sel})} style={{padding:"4px 8px",background:"#EF4444",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Averia</button>}
                  {RC.includes(user.rol) && <button onClick={()=>setForm({tipo:sel.tipo,edit:sel})} style={{padding:"4px 8px",background:"#1E7BBF",color:"#fff",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Editar</button>}
                  {RC.includes(user.rol) && <button onClick={()=>delEq(sel.tipo,sel.id)} style={{padding:"4px 8px",background:"#FEE2E2",color:"#991B1B",border:"none",borderRadius:4,fontSize:11,cursor:"pointer"}}>Eliminar</button>}
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </LoadScript>
    </div>
  );
}
